<template>
  <section class="media-finder">
    <header class="media-finder__header">
      <div class="is-flex is-justify-content-end" style="width: 100%">
        <b-field grouped>
          <p class="control">
            <b-button
                :type="viewMode === 'grid' ? 'is-primary' : 'is-light'"
                icon-left="th"
                @click="viewMode = 'grid'"
            >
              Cuadricula
            </b-button>
          </p>
          <p class="control">
            <b-button
                :type="viewMode === 'list' ? 'is-primary' : 'is-light'"
                icon-left="list"
                @click="viewMode = 'list'"
            >
              Lista
            </b-button>
          </p>
        </b-field>
      </div>
    </header>

    <div v-if="notifications.length" class="mb-4">
      <b-notification
          v-for="message in notifications"
          :key="message"
          type="is-danger"
          has-icon
          aria-close-label="Cerrar"
          @close="removeNotification(message)"
      >
        {{ message }}
      </b-notification>
    </div>

    <div class="is-flex is-justify-content-center">
      <b-upload
          v-model="uploadModel"
          drag-drop
          multiple
          accept="application/pdf,image/*"
          @input="handleUploadSelection"
      >
        <section class="section upload-dropzone has-text-centered">
          <b-icon icon="upload" size="is-large" class="upload-icon" />
          <p>Haz clic o arrastra archivos aqui</p>
          <p class="is-size-7 has-text-grey">Solo PDF e imagenes</p>
          <p class="is-size-7 has-text-grey">Máximo 10 MB por archivo</p>
        </section>
      </b-upload>
    </div>

    <section v-if="pendingFiles.length" class="box pending-queue">
      <div class="pending-queue__header">
        <h4 class="title is-6 mb-0">Pendientes por subir</h4>
        <b-button
            type="is-primary"
            size="is-small"
            :loading="isUploading"
            :disabled="isUploading"
            @click="uploadAllFiles"
        >
          Subir todos
        </b-button>
      </div>

      <div
          v-for="pendingFile in pendingFiles"
          :key="pendingFile.id"
          class="pending-item"
      >
        <div class="pending-item__main">
          <figure class="pending-item__preview">
            <img
                v-if="isImage(pendingFile) && pendingFile.thumbnailUrl"
                :src="pendingFile.thumbnailUrl"
                :alt="pendingFile.name"
            >
            <b-icon
                v-else-if="isPdf(pendingFile)"
                icon="file-pdf-box"
                size="is-large"
                type="is-danger"
            />
            <b-icon v-else icon="file" size="is-large" />
          </figure>

          <div class="pending-item__meta">
            <p class="has-text-weight-semibold">
              {{ pendingFile.name }}
              <b-icon
                  v-if="pendingFile.status === 'success'"
                  icon="check-circle"
                  size="is-small"
                  type="is-success"
              />
              <b-icon
                  v-else-if="pendingFile.status === 'error'"
                  icon="alert-circle"
                  size="is-small"
                  type="is-danger"
              />
            </p>
            <p class="is-size-7 has-text-grey">
              {{ formatSize(pendingFile.size) }} - {{ formatDate(pendingFile.uploadedAt) }}
            </p>
            <b-progress
                :value="pendingFile.progress"
                :type="progressType(pendingFile.status)"
                size="is-small"
                show-value
                format="percent"
            />
          </div>
        </div>

        <b-button
            size="is-small"
            type="is-text"
            icon-left="close"
            :disabled="pendingFile.status === 'uploading'"
            @click="removePendingFile(pendingFile.id)"
        />
      </div>
    </section>

    <section v-if="viewMode === 'grid'" class="file-grid">
      <article
          v-for="fileItem in files"
          :key="fileItem.id"
          class="file-card"
          @click="openPreview(fileItem)"
      >
        <div class="file-card__preview">
          <img
              v-if="isImage(fileItem) && fileItem.thumbnailUrl"
              :src="fileItem.thumbnailUrl"
              :alt="fileItem.name"
          >
          <b-icon
              v-else-if="isPdf(fileItem)"
              icon="file-pdf"
              size="is-large"
              type="is-danger"
          />
          <b-icon v-else icon="file" size="is-large" />
        </div>

        <div class="file-card__body">
          <div class="file-card__name-row" @click.stop>
            <b-input
                v-if="editingFileId === fileItem.id"
                v-model="editingName"
                size="is-small"
                @keyup.enter.native="saveRename(fileItem)"
                @blur="saveRename(fileItem)"
            />
            <p v-else class="file-card__name">{{ fileItem.name }}</p>

            <b-button
                size="is-small"
                type="is-text"
                icon-left="pencil"
                @click="startRename(fileItem)"
            />
          </div>

          <p class="is-size-7 has-text-grey">
            {{ formatSize(fileItem.size) }} - {{ formatDate(fileItem.uploadedAt) }}
          </p>

          <div class="file-card__share" @click.stop>
            <b-checkbox
                :value="fileItem.sharedWithPatient"
                @input="toggleShare(fileItem, $event)"
            >
              Compartir con paciente
            </b-checkbox>
          </div>

          <div class="file-card__actions" @click.stop>
            <b-button size="is-small" type="is-text" icon-left="download" @click="downloadFile(fileItem)">
              Descargar
            </b-button>
            <b-button size="is-small" type="is-text" icon-left="pencil" @click="startRename(fileItem)">
              Renombrar
            </b-button>
            <b-button size="is-small" type="is-text" icon-left="delete" @click="confirmDelete(fileItem)">
              Eliminar
            </b-button>
          </div>
        </div>
      </article>

      <p v-if="!files.length" class="has-text-grey has-text-centered py-5">
        No hay archivos cargados.
      </p>
    </section>

    <section v-else class="file-list">
      <b-table
          :data="files"
          narrowed
          hoverable
          @dblclick="openPreview"
      >
        <b-table-column field="preview" label="Tipo" width="90" v-slot="props">
          <img
              v-if="isImage(props.row) && props.row.thumbnailUrl"
              :src="props.row.thumbnailUrl"
              :alt="props.row.name"
              class="table-thumbnail"
          >
          <b-icon
              v-else-if="isPdf(props.row)"
              icon="file-pdf-box"
              type="is-danger"
          />
          <b-icon v-else icon="file" />
        </b-table-column>

        <b-table-column field="name" label="Nombre" v-slot="props">
          <div class="list-name-cell" @click.stop>
            <b-input
                v-if="editingFileId === props.row.id"
                v-model="editingName"
                size="is-small"
                @keyup.enter.native="saveRename(props.row)"
                @blur="saveRename(props.row)"
            />
            <span v-else>{{ props.row.name }}</span>
            <b-button
                size="is-small"
                type="is-text"
                icon-left="pencil"
                @click="startRename(props.row)"
            />
          </div>
        </b-table-column>

        <b-table-column field="size" label="Tamano" v-slot="props">
          {{ formatSize(props.row.size) }}
        </b-table-column>

        <b-table-column field="uploadedAt" label="Fecha" v-slot="props">
          {{ formatDate(props.row.uploadedAt) }}
        </b-table-column>

        <b-table-column field="sharedWithPatient" label="Compartir" v-slot="props">
          <div @click.stop>
            <b-checkbox
                :value="props.row.sharedWithPatient"
                @input="toggleShare(props.row, $event)"
            />
          </div>
        </b-table-column>

        <b-table-column field="actions" label="Acciones" v-slot="props">
          <div class="table-actions" @click.stop>
            <b-button
                size="is-small"
                type="is-text"
                icon-left="download"
                @click="downloadFile(props.row)"
            />
            <b-button
                size="is-small"
                type="is-text"
                icon-left="trash"
                @click="confirmDelete(props.row)"
            />
          </div>
        </b-table-column>

        <template #empty>
          <p class="has-text-centered has-text-grey">No hay archivos cargados.</p>
        </template>
      </b-table>
    </section>

    <b-modal :active.sync="isPreviewModalActive" has-modal-card trap-focus>
      <div class="modal-card media-preview-modal" v-if="selectedFile">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ selectedFile.name }}</p>
          <button type="button" class="delete" aria-label="close" @click="closePreview" />
        </header>

        <section class="modal-card-body">
          <div class="preview-share mb-3" @click.stop>
            <b-checkbox
                :value="selectedFile.sharedWithPatient"
                @input="toggleShare(selectedFile, $event)"
            >
              Compartir con paciente
            </b-checkbox>
          </div>

          <div v-if="isPdf(selectedFile)" class="pdf-preview-container">
            <iframe
                :src="selectedFile.url"
                class="pdf-preview"
                title="PDF Preview"
            />
          </div>

          <div v-else-if="isImage(selectedFile)" class="image-preview-wrapper">
            <div class="image-preview-controls">
              <b-button size="is-small" type="is-light" icon-left="minus" @click="changeZoom(-0.1)" />
              <span class="is-size-7">{{ Math.round(imageZoom * 100) }}%</span>
              <b-button size="is-small" type="is-light" icon-left="plus" @click="changeZoom(0.1)" />
            </div>
            <div class="image-preview-scroll">
              <img
                  :src="selectedFile.url"
                  :alt="selectedFile.name"
                  class="full-image-preview"
                  :style="{ transform: 'scale(' + imageZoom + ')' }"
              >
            </div>
          </div>

          <p v-else class="has-text-grey">Vista previa no disponible para este tipo de archivo.</p>
        </section>
      </div>
    </b-modal>
  </section>
</template>

<script>
const TEN_MB_BYTES = 10 * 1024 * 1024;

export default {
  name: "MediaFinder",
  props: {
    initialFiles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      files: [],
      pendingFiles: [],
      notifications: [],
      viewMode: "list",
      editingFileId: null,
      editingName: "",
      isPreviewModalActive: false,
      selectedFile: null,
      imageZoom: 1,
      isUploading: false,
      uploadModel: [],
      pendingObjectUrls: new Set()
    };
  },
  watch: {
    initialFiles: {
      handler(newFiles) {
        if (Array.isArray(newFiles)) {
          this.files = newFiles.map((fileItem) => ({ ...fileItem }));
        }
      },
      immediate: true,
      deep: true
    }
  },
  beforeDestroy() {
    this.pendingObjectUrls.forEach((objectUrl) => {
      URL.revokeObjectURL(objectUrl);
    });
    this.pendingObjectUrls.clear();
  },
  methods: {
    isPdf(fileItem) {
      return fileItem && fileItem.mimeType === "application/pdf";
    },
    isImage(fileItem) {
      return fileItem && typeof fileItem.mimeType === "string" && fileItem.mimeType.startsWith("image/");
    },
    formatSize(bytes) {
      if (!bytes || Number.isNaN(Number(bytes))) {
        return "0 KB";
      }

      const parsedBytes = Number(bytes);
      if (parsedBytes < 1024 * 1024) {
        return `${(parsedBytes / 1024).toFixed(1)} KB`;
      }

      return `${(parsedBytes / (1024 * 1024)).toFixed(2)} MB`;
    },
    formatDate(dateValue) {
      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) {
        return "Sin fecha";
      }

      return parsedDate.toLocaleDateString("es-MX", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    validateFile(file) {
      const isValidMimeType = file.type === "application/pdf" || file.type.startsWith("image/");
      if (!isValidMimeType) {
        return {
          isValid: false,
          message: `${file.name}: tipo de archivo no permitido.`
        };
      }

      if (file.size > TEN_MB_BYTES) {
        return {
          isValid: false,
          message: `${file.name}: supera el limite de 10 MB.`
        };
      }

      return {
        isValid: true,
        message: ""
      };
    },
    generateThumbnail(file) {
      if (!file || !file.type || !file.type.startsWith("image/")) {
        return null;
      }

      const thumbnailUrl = URL.createObjectURL(file);
      this.pendingObjectUrls.add(thumbnailUrl);
      return thumbnailUrl;
    },
    handleUploadSelection(selection) {
      const selectedFiles = Array.isArray(selection) ? selection : [selection];

      selectedFiles.filter(Boolean).forEach((selectedFile) => {
        // b-upload multiple returns the full accumulated list on every change,
        // so skip files already queued (matched by name + size + lastModified).
        const alreadyQueued = this.pendingFiles.some(
            (item) =>
                item.file &&
                item.file.name === selectedFile.name &&
                item.file.size === selectedFile.size &&
                item.file.lastModified === selectedFile.lastModified
        );
        if (alreadyQueued) {
          return;
        }

        const validation = this.validateFile(selectedFile);
        if (!validation.isValid) {
          this.pushNotification(validation.message);
          return;
        }

        const pendingUrl = URL.createObjectURL(selectedFile);
        this.pendingObjectUrls.add(pendingUrl);

        this.pendingFiles.push({
          id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: selectedFile.name,
          mimeType: selectedFile.type,
          size: selectedFile.size,
          uploadedAt: new Date().toISOString(),
          url: pendingUrl,
          thumbnailUrl: this.generateThumbnail(selectedFile),
          sharedWithPatient: false,
          file: selectedFile,
          progress: 0,
          status: "idle"
        });
      });

      // Clear b-upload's internal selection; pendingFiles is now the source of truth.
      this.$nextTick(() => {
        this.uploadModel = [];
      });
    },
    pushNotification(message) {
      if (!this.notifications.includes(message)) {
        this.notifications.push(message);
      }

      this.$buefy.toast.open({
        message,
        type: "is-danger"
      });
    },
    removeNotification(message) {
      this.notifications = this.notifications.filter((item) => item !== message);
    },
    removePendingFile(pendingId) {
      const pendingFile = this.pendingFiles.find((item) => item.id === pendingId);
      if (pendingFile) {
        this.cleanupPendingUrls(pendingFile);
      }

      this.pendingFiles = this.pendingFiles.filter((item) => item.id !== pendingId);
    },
    cleanupPendingUrls(pendingFile) {
      [pendingFile.url, pendingFile.thumbnailUrl].forEach((objectUrl) => {
        if (objectUrl && this.pendingObjectUrls.has(objectUrl)) {
          URL.revokeObjectURL(objectUrl);
          this.pendingObjectUrls.delete(objectUrl);
        }
      });
    },
    uploadAllFiles() {
      if (!this.pendingFiles.length) {
        return;
      }
      // Hand the raw pending files to the parent; the parent drives the upload
      // and reports progress/results back through the public methods below.
      this.$emit("upload", this.pendingFiles.slice());
    },

    /**
     * Public API for the parent — called via $refs.
     */
    setUploading(value) {
      this.isUploading = Boolean(value);
    },
    setPendingProgress(pendingId, progress) {
      const target = this.pendingFiles.find((item) => item.id === pendingId);
      if (target) {
        this.$set(target, "progress", progress);
      }
    },
    setPendingStatus(pendingId, status) {
      // status: 'idle' | 'uploading' | 'success' | 'error'
      const target = this.pendingFiles.find((item) => item.id === pendingId);
      if (target) {
        this.$set(target, "status", status);
      }
    },
    resolvePendingFile(pendingId) {
      const pendingFile = this.pendingFiles.find((item) => item.id === pendingId);
      if (pendingFile) {
        this.cleanupPendingUrls(pendingFile);
      }
      this.pendingFiles = this.pendingFiles.filter((item) => item.id !== pendingId);
    },
    progressType(status) {
      if (status === "success") return "is-success";
      if (status === "error") return "is-danger";
      return "is-primary";
    },

    confirmDelete(fileItem) {
      this.$buefy.dialog.confirm({
        title: "Eliminar archivo",
        message: `Se eliminara <b>${fileItem.name}</b>. Esta accion no se puede deshacer.`,
        confirmText: "Eliminar",
        cancelText: "Cancelar",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => {
          this.files = this.files.filter((item) => item.id !== fileItem.id);
          this.$emit("delete", fileItem.id);
          if (this.selectedFile && this.selectedFile.id === fileItem.id) {
            this.closePreview();
          }
        }
      });
    },
    startRename(fileItem) {
      this.editingFileId = fileItem.id;
      this.editingName = fileItem.name;
    },
    saveRename(fileItem) {
      if (this.editingFileId !== fileItem.id) {
        return;
      }

      const trimmedName = this.editingName.trim();
      if (!trimmedName) {
        this.editingName = fileItem.name;
      } else if (trimmedName !== fileItem.name) {
        const targetFile = this.files.find((item) => item.id === fileItem.id);
        if (targetFile) {
          targetFile.name = trimmedName;
        }

        if (this.selectedFile && this.selectedFile.id === fileItem.id) {
          this.selectedFile.name = trimmedName;
        }

        this.$emit("rename", { id: fileItem.id, name: trimmedName });
      }

      this.editingFileId = null;
      this.editingName = "";
    },
    toggleShare(fileItem, sharedValue) {
      const targetFile = this.files.find((item) => item.id === fileItem.id);
      if (targetFile) {
        targetFile.sharedWithPatient = Boolean(sharedValue);
      }

      if (this.selectedFile && this.selectedFile.id === fileItem.id) {
        this.selectedFile.sharedWithPatient = Boolean(sharedValue);
      }

      this.$emit("toggle-share", {
        id: fileItem.id,
        sharedWithPatient: Boolean(sharedValue)
      });
    },
    downloadFile(fileItem) {
      if (!fileItem.url) {
        this.pushNotification(`No se pudo descargar ${fileItem.name}.`);
        return;
      }

      const link = document.createElement("a");
      link.href = fileItem.url;
      link.download = fileItem.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    openPreview(fileItem) {
      this.selectedFile = fileItem;
      this.imageZoom = 1;
      this.isPreviewModalActive = true;
    },
    closePreview() {
      this.isPreviewModalActive = false;
      this.selectedFile = null;
      this.imageZoom = 1;
    },
    changeZoom(step) {
      const nextValue = Math.max(0.3, Math.min(3, this.imageZoom + step));
      this.imageZoom = Number(nextValue.toFixed(2));
    }
  }
};
</script>

<style scoped>
.media-finder {
  --lapi-primary: #e8850c;
  --lapi-secondary: #5b8fa3;
}

.media-finder ::v-deep .button.is-primary {
  background-color: var(--lapi-primary);
  border-color: var(--lapi-primary);
}

.media-finder ::v-deep .button.is-primary:hover {
  background-color: #d1790b;
  border-color: #d1790b;
}

.media-finder ::v-deep .checkbox input:checked + .check {
  background: var(--lapi-secondary);
  border-color: var(--lapi-secondary);
}

.media-finder ::v-deep progress.progress.is-primary::-webkit-progress-value {
  background-color: var(--lapi-primary);
}

.media-finder ::v-deep progress.progress.is-primary::-moz-progress-bar {
  background-color: var(--lapi-primary);
}

.media-finder__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.upload-dropzone {
  border: 2px dashed rgba(91, 143, 163, 0.45);
  border-radius: 12px;
  cursor: pointer;
  padding: 1.25rem 1rem;
}

.upload-icon {
  color: var(--lapi-primary);
  margin-bottom: 0.5rem;
}

.pending-queue {
  margin-top: 1rem;
}

.pending-queue__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.pending-item {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.pending-item__main {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  width: calc(100% - 3rem);
}

.pending-item__preview {
  align-items: center;
  background: #f7f8fa;
  border-radius: 8px;
  display: flex;
  height: 56px;
  justify-content: center;
  margin: 0;
  width: 56px;
}

.pending-item__preview img {
  border-radius: 6px;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.pending-item__meta {
  flex: 1;
}

.file-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  margin-top: 1rem;
}

.file-card {
  background: #fff;
  border: 1px solid #edf0f3;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 280px;
  transition: box-shadow 0.2s ease;
}

.file-card:hover {
  box-shadow: 0 8px 24px rgba(91, 143, 163, 0.18);
}

.file-card__preview {
  align-items: center;
  background: #f7f9fb;
  border-bottom: 1px solid #edf0f3;
  border-radius: 12px 12px 0 0;
  display: flex;
  height: 140px;
  justify-content: center;
  overflow: hidden;
}

.file-card__preview img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.file-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
}

.file-card__name-row {
  align-items: center;
  display: flex;
  gap: 0.25rem;
  justify-content: space-between;
}

.file-card__name {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
}

.file-card__share {
  margin-top: auto;
}

.file-card__actions {
  display: flex;
  justify-content: space-between;
}

.file-list {
  margin-top: 1rem;
}

.table-thumbnail {
  border-radius: 4px;
  height: 36px;
  object-fit: cover;
  width: 36px;
}

.list-name-cell {
  align-items: center;
  display: flex;
  gap: 0.35rem;
}

.table-actions {
  display: flex;
  gap: 0.25rem;
}

.media-preview-modal {
  max-height: 90vh;
  width: min(1000px, 92vw);
}

.preview-share {
  border-bottom: 1px solid #edf0f3;
  padding-bottom: 0.75rem;
}

.pdf-preview-container {
  border: 1px solid #edf0f3;
  border-radius: 8px;
  height: 70vh;
  overflow: hidden;
}

.pdf-preview {
  border: 0;
  height: 100%;
  width: 100%;
}

.image-preview-wrapper {
  border: 1px solid #edf0f3;
  border-radius: 8px;
  padding: 0.75rem;
}

.image-preview-controls {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.image-preview-scroll {
  max-height: 68vh;
  overflow: auto;
}

.full-image-preview {
  display: block;
  margin: 0 auto;
  transform-origin: top center;
}

@media (max-width: 768px) {
  .media-finder__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
  }

  .file-card__actions {
    flex-wrap: wrap;
    gap: 0.25rem;
  }
}
</style>